'use client'

import { useEffect, useState } from 'react'
import BaseCountdown, { zeroPad, CountdownRendererFn, CountdownProps as BaseCountdownProps } from 'react-countdown'
import { mergeObjects, repeat } from '@utils/objects';
import { parseDate } from '@utils/dates';

export type CountdownProps = BaseCountdownProps & {
  date: Date | string;
  labels?: false | {
    days?: { singular?: string; plural?: string; },
    hours?: { singular?: string; plural?: string; },
    minutes?: { singular?: string; plural?: string; },
    seconds?: { singular?: string; plural?: string; },
  };
  className?: string;
  showDays?: boolean;
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
  showCompleted?: boolean;
  hideOnCompleted?: boolean;
};

export const Countdown = ({children, ...props}: CountdownProps) => {
  // @link {https://nextjs.org/docs/messages/react-hydration-error}
  const [isClient, setIsClient] = useState(false)
  useEffect(() => { setIsClient(true) }, []);

  if (! isClient) {
    return <div className="countdown-wrapper" />;
  }

  const defaultProps: Omit<CountdownProps, 'date'> = {
    labels: {
      days: {
        singular: 'DIA',
        plural: 'DIAS',
      },
      hours: {
        singular: 'HORA',
        plural: 'HORAS',
      },
      minutes: {
        singular: 'MIN',
        plural: 'MINS',
      },
      seconds: {
        singular: 'SEG',
        plural: 'SEGS',
      },
    },
    className: '',
    showDays: true,
    showHours: true,
    showMinutes: true,
    showSeconds: true,
    showCompleted: false,
    hideOnCompleted: false,
    autoStart: true,
  };

  const {
    date,
    labels,
    className,
    showDays,
    showHours,
    showMinutes,
    showSeconds,
    showCompleted,
    hideOnCompleted,
    ...countdownProps
  } = mergeObjects(defaultProps, props);

  const renderer: CountdownRendererFn = ({ days, hours, minutes, seconds, completed }) => {
    if (completed && showCompleted) {
      return (
        <div className="countdown-wrapper">
          {children}
        </div>
      );
    }

    if (completed && hideOnCompleted) {
      return <></>
    }

    const zeroPadTime = countdownProps?.zeroPadTime ?? 2;
    const zeroPadDays = countdownProps?.zeroPadDays ?? zeroPadTime;

    const numbersForDays = repeat((days + 1), (v) => zeroPad(v, zeroPadDays)).join('\n');
    const numbersForHours = repeat(24, (v) => zeroPad(v, zeroPadTime)).join('\n');
    const numbersForMinutes = repeat(60, (v) => zeroPad(v, zeroPadTime)).join('\n');
    const numbersForSeconds = repeat(60, (v) => zeroPad(v, zeroPadTime)).join('\n');

    const className = [
      props.className,
      'countdown-wrapper',
      showDays && 'has-days',
      showHours && 'has-hours',
      showMinutes && 'has-minutes',
      showSeconds && 'has-seconds',
      `has-${ [showDays, showHours, showMinutes, showSeconds].filter(Boolean).length }-elements`,
    ].filter(Boolean).join(' ');

    return (
      <div className={className}>
        { showDays && (
          <div className="number-wrapper">
            <span className="number countdown-animation">
              <span className="countdown-animation-numbers" style={{"--value": days} as any}>
                { numbersForDays }
              </span>
            </span>
            { labels && (
              <span className="number-label countdown-animation">
                <span className="countdown-animation-numbers" style={{"--value": ( days == 1 ? 0 : 1 )} as any}>
                  { `${labels.days.singular}\n${labels.days.plural}` }
                </span>
              </span>
            ) }
          </div>
        ) }
        { showHours && (
          <div className="number-wrapper">
            <span className="number countdown-animation">
              <span className="countdown-animation-numbers" style={{"--value": hours} as any}>
                { numbersForHours }
              </span>
            </span>
            { labels && (
              <span className="number-label countdown-animation">
                <span className="countdown-animation-numbers" style={{"--value": ( hours == 1 ? 0 : 1 )} as any}>
                  { `${labels.hours.singular}\n${labels.hours.plural}` }
                </span>
              </span>
            ) }
          </div>
        ) }
        { showMinutes && (
          <div className="number-wrapper">
            <span className="number countdown-animation">
              <span className="countdown-animation-numbers" style={{"--value": minutes} as any}>
                { numbersForMinutes }
              </span>
            </span>
            { labels && (
              <span className="number-label countdown-animation">
                <span className="countdown-animation-numbers" style={{"--value": ( minutes == 1 ? 0 : 1 )} as any}>
                  { `${labels.minutes.singular}\n${labels.minutes.plural}` }
                </span>
              </span>
            ) }
          </div>
        ) }
        { showSeconds && (
          <div className="number-wrapper">
            <span className="number countdown-animation">
              <span className="countdown-animation-numbers" style={{"--value": seconds} as any}>
                { numbersForSeconds }
              </span>
            </span>
            { labels && (
              <span className="number-label countdown-animation">
                <span className="countdown-animation-numbers" style={{"--value": ( seconds == 1 ? 0 : 1 )} as any}>
                  { `${labels.seconds.singular}\n${labels.seconds.plural}` }
                </span>
              </span>
            ) }
          </div>
        ) }
      </div>
    )
  };

  countdownProps.date = (typeof date === 'string') ? parseDate(date, 'yyyy-MM-dd HH:mm:ss') : date;
  countdownProps.renderer = countdownProps?.renderer || renderer;

  return <BaseCountdown {...countdownProps}>{ children }</BaseCountdown>;
}

export default Countdown;
